import { useMemo } from 'react'
import type { LabelOrientation } from '@api/fastAPI.schemas'
import { useGetPlanLabelPositions } from '@api/plan-label-positions/plan-label-positions'
import { usePersistentParams } from 'src/hooks'

export interface PlanLabelPositionOverride {
  x: number
  y: number
  /** user-rotated orientation; unset keeps the automatic orientation */
  orientation?: LabelOrientation
}

/**
 * Persisted (user-moved / user-rotated) plan label positions by element guid.
 *
 * Deliberately without the in-progress drag position: the batched floorplan
 * labels rebuild their whole text batch whenever their inputs change, so a
 * label being dragged is hidden there (`useHideLabels`) and drawn on its own
 * in `PlanLabelController` instead, tracking the drag locally.
 */
const usePlanLabelPositionOverrides = (): Record<string, PlanLabelPositionOverride> => {
  const { projectId }: { projectId?: string } = usePersistentParams()
  const { data: positions } = useGetPlanLabelPositions(projectId as string)

  return useMemo(() => {
    const overrides: Record<string, PlanLabelPositionOverride> = {}
    for (const position of positions ?? []) {
      overrides[position.elementGuid] = {
        x: position.x,
        y: position.y,
        orientation: position.orientation ?? undefined,
      }
    }
    return overrides
  }, [positions])
}

export default usePlanLabelPositionOverrides
